/**
 * EventsReportGenerator.js
 * Utility for generating CSV reports from event data
 */

import Data from '@/api/data';

class EventsReportGenerator {
  /**
   * Generate a raw CSV report from event data (all data in one file)
   * @param {Object} eventsData - Object containing event tables data
   * @param {String} dataID - The ID of the data source
   * @returns {void} - Triggers a file download
   */
  static generateRawReport(events, dataID) {
    // Grab the columns details
    const columnNames = Object.keys(events[0]);
    
    // Set up the data structure
    const orginizedData = {};
    for (const column of columnNames) {
        orginizedData[column] = [];
    }

    // Populate the data structure
    for (const event of events) {
        for (const columnName of columnNames) {
            orginizedData[columnName].push(event[columnName]);
        }
    }
    
    // Download the CSV
    this.downloadCSV(orginizedData, `${dataID}_events_raw`);
  }
  
  /**
   * Generate a custom CSV report based on a template
   * @param {Object} reportTemplate - Template defining report title and columns
   * @param {Object} events - Object containing event tables data
   * @param {String} dataID - The ID of the data source
   * @returns {void} - Triggers a file download
   */
  static async generateCustomeReport(fileName, details, events, dataID) {
    // Grab the columns details
    const columnNames = details.columns;
    const columnValues = details.values;
    const columnFormats = details.formats;
    const includeCoordinates = details.includeCoordinates;
    
    // Set up the data structure
    const orginizedData = {};
    for (const column of columnNames) {
        orginizedData[column] = [];
    }

    // Populate the data structure
    for (const event of events) {
        for (const index in columnNames) {
            const columnName = columnNames[index];
            const valueKey = columnValues[index];
            const format = columnFormats[index];
            let value = event[valueKey];
            
            // Apply formatting based on the specified format
            value = this.formatValue(value, format);
            
            orginizedData[columnName].push(value);
        }
    }

    // Add location data if requested
    if (includeCoordinates) {
        orginizedData['latitude'] = [];
        orginizedData['longitude'] = [];
        
        for (const event of events) {
            const body = {
                dataID: dataID,
                subject: 'gpgga_Internal',
                filters: [
                    {
                        field: 'messageUTC',
                        operator: '>=',
                        value: event.startUTC
                    },
                    {
                        field: 'messageUTC',
                        operator: '<=',
                        value: event.endUTC
                    }
                ],
                sort: [
                    {
                        field: 'messageUTC',
                        direction: 'asc'
                    }
                ]
            }
            const locationData = await Data.post(body);
            // Store all the latitude and longitude values
            let latitude = [];
            let longitude = [];
            locationData.forEach(location => {
                latitude.push(location.lat);
                longitude.push(location.lon);
            });
            orginizedData['latitude'].push(JSON.stringify(latitude));
            orginizedData['longitude'].push(JSON.stringify(longitude));
        }
    }
    
    // Generate a safe filename from the report title
    const safeName = fileName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
    
    // Download the CSV
    this.downloadCSV(orginizedData, `${dataID}_${safeName}`);
  }
  
  /**
   * Format a value based on the specified format
   * @param {any} value - The value to format
   * @param {String} format - The format to apply
   * @returns {String} - The formatted value
   * @private
   */
  static formatValue(value, format) {
    if (value === null || value === undefined) {
      return '';
    }
    
    // Handle string formats
    if (typeof value === 'string') {
      // Date formats
      if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const [year, month, day] = value.split('-');
        
        switch (format) {
          case 'DD-MM-YYYY': return `${day}-${month}-${year}`;
          case 'DD/MM/YYYY': return `${day}/${month}/${year}`;
          case 'MM-DD-YYYY': return `${month}-${day}-${year}`;
          case 'MM/DD/YYYY': return `${month}/${day}/${year}`;
          case 'YYYY/MM/DD': return `${year}/${month}/${day}`;
          default: return value; // YYYY-MM-DD is default
        }
      }
      
      // Time formats
      if (/^\d{2}:\d{2}:\d{2}$/.test(value) || /^(\d+) Days? (\d{2}:\d{2}:\d{2})$/.test(value)) {
        let days = 0;
        let timeStr = value;
        
        // Extract days if present
        const daysMatch = value.match(/^(\d+) Days? (\d{2}:\d{2}:\d{2})$/);
        if (daysMatch) {
          days = parseInt(daysMatch[1]);
          timeStr = daysMatch[2];
        }
        
        const [hours, minutes, seconds] = timeStr.split(':').map(Number);
        const totalHours = days * 24 + hours;
        
        switch (format) {
          case 'HH:MM:SS': 
            // Convert days to hours and add to existing hours
            return `${totalHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
          case 'HH:MM': 
            return `${totalHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
          case 'MM:SS': 
            return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
          case 'hours': 
            return `${totalHours}`;
          case 'minutes': 
            return `${totalHours * 60 + minutes}`;
          case 'seconds': 
            return `${(totalHours * 60 + minutes) * 60 + seconds}`;
          case 'milliseconds': 
            return `${((totalHours * 60 + minutes) * 60 + seconds) * 1000}`;
          default: 
            return value; // days HH:MM:SS is default
        }
      }
      
      // Date and time formats
      if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(value)) {
        const [datePart, timePart] = value.split(' ');
        const [year, month, day] = datePart.split('-');
        
        switch (format) {
          case 'DD-MM-YYYY HH:MM:SS': return `${day}-${month}-${year} ${timePart}`;
          case 'DD/MM/YYYY HH:MM:SS': return `${day}/${month}/${year} ${timePart}`;
          case 'MM-DD-YYYY HH:MM:SS': return `${month}-${day}-${year} ${timePart}`;
          case 'MM/DD/YYYY HH:MM:SS': return `${month}/${day}/${year} ${timePart}`;
          case 'YYYY/MM/DD HH:MM:SS': return `${year}/${month}/${day} ${timePart}`;
          default: return value; // YYYY-MM-DD HH:MM:SS is default
        }
      }
      
      // General string formats
      switch (format) {
        case 'uppercase': return value.toUpperCase();
        case 'lowercase': return value.toLowerCase();
        case 'capitalize': return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
        default: return value; // 'string' is default
      }
    }
    
    // Handle number formats
    if (typeof value === 'number') {
      if (Number.isInteger(value)) {
        return format === 'string' ? String(value) : value;
      } else {
        if (format === 'scientific') {
          return value.toExponential();
        } else if (format.includes('Decimal')) {
          const decimalPlaces = parseInt(format.split(' ')[0]);
          return value.toFixed(decimalPlaces);
        }
        return value;
      }
    }
    
    // Handle boolean formats
    if (typeof value === 'boolean') {
      switch (format) {
        case 'yes/no': return value ? 'yes' : 'no';
        case 'true/false': return value ? 'true' : 'false';
        case '1/0': return value ? '1' : '0';
        default: return String(value); // 'boolean' is default
      }
    }
    
    // Default: return as is
    return value;
  }
  
  /**
   * Convert data object to CSV string and download as a file
   * @param {Object} data - Object with column names as keys and arrays of values
   * @param {String} fileName - Name for the file (without extension)
   * @private
   */
  static downloadCSV(data, fileName) {
    // Create headers and rows
    const headers = Object.keys(data);
    const rows = [headers.join(',')];
    
    // Get the length of any column (they should all be the same length)
    const rowCount = data[headers[0]]?.length || 0;
    
    // Add data rows
    for (let i = 0; i < rowCount; i++) {
      rows.push(headers.map(header => {
        const value = data[header][i];
        
        // Handle null values
        if (value === null || value === undefined) {
          return '';
        }
        
        // Escape values with commas or quotes
        if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        
        return value;
      }).join(','));
    }
    
    // Download the CSV
    const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${fileName}.csv`;
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}

export default EventsReportGenerator;
